import { createSelector } from 'reselect';
import { SupportedSystems, EMPTY_ARRAY, Routes } from '../../shared';

const mergePartProcess = (process, part) => ({
  ...process,
  timeEntries: process.timeEntries,
  partId: part.id,
  partImageId: part.partId,
  partDescription: part.partDescription,
  partImageBase64: part.partImageBase64,
});

const processXform = (acc, part) => {
  const processes = part.processes.map(i => mergePartProcess(i, part));
  return acc.concat(processes);
};

const selectStartOfCurrentWeek = state => state.projectEntry.projectEntries.selectedDays[0];
const selectEndOfCurrentWeek = state => state.projectEntry.projectEntries.selectedDays[6];
const selectRouterStoreModule = (state) => state.router;
export const selectProjectEntryData = (state) => state.projectEntry.projectEntries.data;

export const selectCurrentSystem = createSelector(selectRouterStoreModule, 
  (router) => { 
    const path = (router && router.location) ? (router.location.pathname).split('/')[1] : null;

    if (!path)
      return null;

    return Routes.VPIN.includes(path)
      ? SupportedSystems.VPIN
      : SupportedSystems[path.toUpperCase()];
  }
);

const isCurrentSystemBulkEntryEnabled = createSelector(selectCurrentSystem,
  (currentSystem) => { 
    return (currentSystem === SupportedSystems.LRS) 
      || (currentSystem === SupportedSystems.VPIN) 
      || (currentSystem === SupportedSystems.GPRS)
  }
);

const isAddTaskButtonEnabled = createSelector(selectCurrentSystem,
  currentSystem => currentSystem === SupportedSystems.LRS || currentSystem === SupportedSystems.VPIN
);

const lrsEntries = createSelector(selectProjectEntryData, selectCurrentSystem,
  (data, currentSystem) => {
    if (currentSystem !== SupportedSystems.LRS)
      return EMPTY_ARRAY;

    return data.filter(q => q.type === SupportedSystems.LRS)
  }
)
const vpinEntries = createSelector(
  [selectProjectEntryData, selectEndOfCurrentWeek, selectCurrentSystem],
  (data, endOfCurrentWeek, currentSystem) => {
    if (currentSystem !== SupportedSystems.VPIN)
      return EMPTY_ARRAY;

    return data.filter(q => q.type === SupportedSystems.VPIN &&
      q.activeDate && new Date(q.activeDate) <= new Date(endOfCurrentWeek));
  }
)
const gprsEntries = createSelector(selectProjectEntryData, selectCurrentSystem,
  (data, currentSystem) => {
    if (currentSystem !== SupportedSystems.GPRS)
      return EMPTY_ARRAY;
    return data
      .filter(entry => entry.type === SupportedSystems.GPRS)
      .map(entry => ({
        ...entry,
        processes: entry.parts.reduce(processXform, [])
      }));
  }
)
const adminEntries = createSelector(selectProjectEntryData,
  data => data.filter(q => q.type === SupportedSystems.ADMIN)
)

const projectEntries = createSelector(
  [lrsEntries, vpinEntries, gprsEntries, adminEntries, selectCurrentSystem],
  (lrs, vpin, gprs, admin, currentSystem) => {
    switch (currentSystem) {
      case SupportedSystems.LRS:
        return lrs.concat(admin);
      case SupportedSystems.VPIN:
        return vpin.concat(admin);
      case SupportedSystems.GPRS:
        return gprs.concat(admin);
      default:
        return EMPTY_ARRAY;
    }
  }
);

const isTimeEntryInCurrentWeek = (startOfWeek, endOfWeek, timeEntry) => {
  const teDate = new Date(timeEntry.date);
  return startOfWeek <= teDate && teDate <= endOfWeek;
}

const currentWeekTimeEntries = createSelector(
  [projectEntries, selectStartOfCurrentWeek, selectEndOfCurrentWeek],
  (projectEntries, startOfWeek, endOfWeek) => {
    return projectEntries.reduce((acc, entry) => {
      switch (entry.type) {
        case SupportedSystems.LRS:
          return entry.procedures.reduce((proceduresAcc, procedure) => proceduresAcc.concat(procedure.timeEntries.filter(te => isTimeEntryInCurrentWeek(startOfWeek, endOfWeek, te))), acc);
        case SupportedSystems.VPIN:
          return acc.concat(entry.timeEntries.filter(te => isTimeEntryInCurrentWeek(startOfWeek, endOfWeek, te)));
        case SupportedSystems.ADMIN:
          return entry.categories.reduce((categoriesAcc, category) => categoriesAcc.concat(category.timeEntries.filter(te => isTimeEntryInCurrentWeek(startOfWeek, endOfWeek, te))), acc);
        case SupportedSystems.GPRS:
          return entry.processes.reduce((processesAcc, process) => processesAcc.concat(process.timeEntries.filter(te => isTimeEntryInCurrentWeek(startOfWeek, endOfWeek, te))), acc);
        default:
          return acc;
      }
    }, []);
  }
);

const initialTotals = createSelector(currentWeekTimeEntries, timeEntries => {
  return timeEntries.reduce((acc, timeEntry) => {
    const dayOfWeek = new Date(timeEntry.date).getDay();
    return (acc[dayOfWeek] = acc[dayOfWeek] + timeEntry.hoursEntered, acc);
  }, Array(7).fill(0));
});

const currentWeekAdminTimeEntries = createSelector(
  [projectEntries, selectStartOfCurrentWeek, selectEndOfCurrentWeek],
  (projectEntries, startOfWeek, endOfWeek) => {
    return projectEntries.reduce((acc, entry) => {
      if (entry.type == SupportedSystems.ADMIN) {
        return entry.categories.reduce((categoriesAcc, category) => categoriesAcc.concat(category.timeEntries.filter(te => isTimeEntryInCurrentWeek(startOfWeek, endOfWeek, te))), acc);
      }

      return acc;
    }, []);
  }
);

const initialAdminTotals = createSelector(currentWeekAdminTimeEntries, timeEntries => {
  return timeEntries.reduce((acc, timeEntry) => {
    const dayOfWeek = new Date(timeEntry.date).getDay();
    return (acc[dayOfWeek] = acc[dayOfWeek] + timeEntry.hoursEntered, acc);
  }, Array(7).fill(0));
});

export const selectors = {
  adminEntries,
  initialTotals,
  initialAdminTotals,
  projectEntries,
  selectCurrentSystem,
  isCurrentSystemBulkEntryEnabled,
  isAddTaskButtonEnabled,
};
